import { AxiosResponse } from "axios";
import appConfig from "../appConfig";
import { ActivityEntryType } from "../types/activityTypes";
import { PaginationedData } from "../types/apiTypes";
import { ChangeClientType, IdType } from "../types/appTypes";
import { GovernmentCaseHandler, Receivable, ReceivableId, ReceivableItemLine, ReceivableTemplate, ReceivableTextLine, StoreTaxLegalCase, TagStoreQuick, TaxLegalCase, UpdateTaxLegalCase } from "../types/taxLegalTypes";
import { createCacheLRU } from "../utilities/simpleCache";
import getApiEndpoint from "./apiEndpoint";
import http from "./httpService";
import { mediaApiEndpoint } from "./mediaService";
import { getApiQuery, reporterCompanyKey } from "./urlQueryService";

/* PERMISSIONS FOR TAX LEGAL: 'screen.tax_legal','screen.tax_legal_index','tax_legal.create','tax_legal.update' */


const apiEndpoint = `${getApiEndpoint()}/taxLegalCase`;

export function taxLegalCaseUrl(id: string | number) {
  return `${apiEndpoint}/${id}`;
}

export function taxLegalCasesURL(query?: string) {
  const q = new URLSearchParams(query)
  if (q.has(reporterCompanyKey, 'dmf')) q.set(reporterCompanyKey, appConfig('companyName'))
  else if (q.has(reporterCompanyKey, 'not-dmf')) q.set(reporterCompanyKey, "<>" + appConfig('companyName'))
  return `${apiEndpoint}?${getApiQuery(q.toString())}`;
}

export function getTaxLegalCase(id: IdType) {
  return http.get(taxLegalCaseUrl(id));
}
export function createTaxLegalCase(data: StoreTaxLegalCase) {
  return http.post<TaxLegalCase>(apiEndpoint, data);
}
export function updateTaxLegalCase(id: string | number, data: UpdateTaxLegalCase) {
  return http.patch<TaxLegalCase>(taxLegalCaseUrl(id), data);
}
export function taxLegalCaseToggleStatus(id: string | number, data: {
  obsoleted?: boolean,
  repairRequested?: boolean,
  allDMRDocumentsRequested?: boolean,
}) {
  return http.patch<TaxLegalCase>(`${taxLegalCaseUrl(id)}/toggleStatus`, data);
}

export function deleteTaxLegalCase(id: IdType, doRefund?: boolean) {
  const refundReason = doRefund ? 5 : -1;
  const url = `${taxLegalCaseUrl(id)}?refundReason=${refundReason}`;
  return http.delete(url);
}


export function taxLegalFileUrl(id?: IdType) {
  return id ? `${taxLegalCaseUrl(id)}/file` : mediaApiEndpoint;
}



/**
 * GET http://localhost:8007/api/v1/taxLegalCase/{taxLegalCase}/downloadAllFiles \
 * Download all files attached the tax legal case.
 */
export function downloadAllFiles(id: IdType) {
  return http.get(`${taxLegalCaseUrl(id)}/downloadAllFiles`);
}

export function updateTaxLegalCaseClient(id: IdType, data: ChangeClientType) {
  return http.put(`${taxLegalCaseUrl(id)}/client`, data);
}



/**
 * PUT taxLegalCase/{taxLegalCase}/restore
 */
export function restoreTaxLegalCase(id: IdType) {
  return http.put(`${taxLegalCaseUrl(id)}/restore`);
}


/**
 * URL taxLegalCase/{taxLegalCase}/activities \
 * URL for CRUD operations on the taxLegalCase acitivities
 */
export function taxLegalActivitiesUrl(id: IdType) {
  return `${taxLegalCaseUrl(id)}/activities`;
}
/**
 * PUT taxLegalCase/{taxLegalCase}/activities/{logEntry} \
 * Update an activity entry on the case
 */
export function updateTaxLegalActivity(id: IdType, data: ActivityEntryType) {
  return http.put(`${taxLegalCaseUrl(id)}/activities/${data.id}`, data);
}

/**
 * DELETE taxLegalCase/{taxLegalCase}/activities/{logEntry} \
 * Delete an activity entry from the case
 */
export function deleteTaxLegalActivity(id: IdType, deleteId: string) {
  return http.delete(`${taxLegalCaseUrl(id)}/activities/${deleteId}`);
}

/**
 * POST taxLegalCase/{taxLegalCase}/activities \
 * Create a new activity entry on the case
 */
export function createTaxLegalActivity(id: IdType, data: ActivityEntryType) {
  return http.post(`${taxLegalCaseUrl(id)}/activities`, data);
}



const governmentCaseHandlerCache = createCacheLRU<AxiosResponse<GovernmentCaseHandler>>(10, 240_000)
/**
 * GET /governmentCaseHandler/{governmentCaseHandler} \
 * Get government case handler of ID
 */
export async function getGovernmentCaseHandler(id: IdType) {
  const cachedResponse = governmentCaseHandlerCache.get(id.toString())
  if (cachedResponse) {
    return cachedResponse
  }
  const res = await http.get<GovernmentCaseHandler>(`${getApiEndpoint()}/governmentCaseHandler/${id}`);
  governmentCaseHandlerCache.set(id.toString(), res)
  return res
}
/**
 * POST /governmentCaseHandler/storeQuick \
 * Create a new DataBase entry of government caseHandler in the system
 */
export function addGovernmentCaseHandler(data: GovernmentCaseHandler) {
  return http.post<GovernmentCaseHandler>(`${getApiEndpoint()}/governmentCaseHandler/storeQuick`, data);
}

/**
 * POST /tag/storeQuick \
 * Create a new DataBase entry of government caseHandler in the system
 */
export function addTaxLegalTag(data: TagStoreQuick) {
  return http.post<GovernmentCaseHandler>(`${getApiEndpoint()}/tag/storeQuick`, data);
}


/**
 * GET /tag/{tag} \
 * Get tag of id
 */
export function getTaxLegalTag(id: IdType) {
  return http.get(`${getApiEndpoint()}/tag/${id}`);
}




/**
 * GET taxLegalCase/{taxLegalCase}/downloadEquipmentReport \
 * Download a file of equipment list attached to the case.
 */
export function getEquipmentList(id: IdType) {
  return http.get<{ url: string }>(`${taxLegalCaseUrl(id)}/downloadEquipmentReport`);
}



/**
 * GET taxLegalCase/initializeExport \
 * It just takes an input  like this: {"to": "asd@asd.asd"} and returns {"data": true} and starts a background job that handles the export and emailing
 */
export function initializeExport(data: { to: string }) {
  return http.post<{ data: boolean }>(`${apiEndpoint}/initializeExport`, data);
}


// GET|HEAD        api/v1/taxLegalCase/receivable/products
export const receivableProductsUrl = `${apiEndpoint}/receivable/products`

export function receivableUrl(taxLegalCaseId: IdType) {
  return `${taxLegalCaseUrl(taxLegalCaseId)}/receivable`
}
export function getReceivables(caseId: IdType) {
  return http.get<PaginationedData<Receivable>>(receivableUrl(caseId));
}
type UpsertReceivablePayload = {
  title: string,
  itemLines: ReceivableItemLine[],
  textLines: ReceivableTextLine[],
  paymentTermsId: number,
}
export function storeReceivable(caseId: IdType, data: UpsertReceivablePayload) {
  return http.post<Receivable>(receivableUrl(caseId), data);
}
export function updateReceivable(caseId: IdType, receivableId: ReceivableId) {
  return http.put<Receivable>(`${receivableUrl(caseId)}/${receivableId}`);
}
export function deleteReceivable(caseId: IdType, receivableId: ReceivableId, data: { forceDelete: boolean }) {
  return http.delete<boolean>(`${receivableUrl(caseId)}/${receivableId}`, { data });
}

// Route::put('taxLegalCase/{taxLegalCase}/receivable/{receivable}/restore', [ReceivableController::class, 'restore'])
//     ->name('taxLegalCase.receivable.restore');
export function restoreReceivable(taxLegalCaseId: IdType, receivableId: ReceivableId) {
  return http.put<Receivable>(`${receivableUrl(taxLegalCaseId)}/${receivableId}/restore`);
}

// Route::post('taxLegalCase/{taxLegalCase}/receivable/{receivable}/publish', ReceivablePublish::class)
//     ->name('taxLegalCase.receivable.publish');
export function publishReceivable(caseId: IdType, receivableId: ReceivableId, paymentTermCode: number | undefined) {
  return http.post<Receivable>(`${receivableUrl(caseId)}/${receivableId}/publish`, { paymentTermId: paymentTermCode });
}

// Route::post('taxLegalCase/{taxLegalCase}/receivable/{receivable}/accept', ReceivableAccept::class)
//     ->name('taxLegalCase.receivable.accept');
export function acceptReceivable(caseId: IdType, receivableId: ReceivableId) {
  return http.post<Receivable>(`${receivableUrl(caseId)}/${receivableId}/accept`);
}

// Route::post('taxLegalCase/{taxLegalCase}/receivable/{receivable}/sendInvoice', ReceivableSendInvoice::class)
//     ->name('taxLegalCase.receivable.sendInvoice');
export function sendInvoiceReceivable(caseId: IdType, receivableId: ReceivableId) {
  return http.post<Receivable>(`${receivableUrl(caseId)}/${receivableId}/sendInvoice`);
}

// Route::post('taxLegalCase/{taxLegalCase}/receivable/{receivable}/markPaid', ReceivableMarkPaid::class)
//     ->name('taxLegalCase.receivable.markPaid');
export function markPaidReceivable(caseId: IdType, receivableId: ReceivableId) {
  return http.post<Receivable>(`${receivableUrl(caseId)}/${receivableId}/markPaid`);
}


export const receivableTemplatesUrl = `${getApiEndpoint()}/taxLegalCaseReceivableTemplate`;
// GET|HEAD        api/v1/taxLegalCaseReceivableTemplate taxLegalCaseReceivabl…
export function getReceivableTemplates() {
  return http.get<PaginationedData<ReceivableTemplate>>(receivableTemplatesUrl);
}

export type UpsertReceivableTemplatePayload = {
  title: string,
  itemLines: ReceivableItemLine[],
  textLines: ReceivableTextLine[],
}
// POST            api/v1/taxLegalCaseReceivableTemplate taxLegalCaseReceivabl…
export function storeReceivableTemplate(template: UpsertReceivableTemplatePayload) {
  return http.post<ReceivableTemplate>(receivableTemplatesUrl, template);
}
// PUT|PATCH       api/v1/taxLegalCaseReceivableTemplate/{taxLegalCaseReceivableTemplate} taxLegalCaseReceivableTemplate.update › TaxLegalReceivableTemplateContr…
export function updateReceivableTemplate(templateId: ReceivableId, template: UpsertReceivableTemplatePayload) {
  return http.put<ReceivableTemplate>(`${receivableTemplatesUrl}/${templateId}`, template);
}

// DELETE          api/v1/taxLegalCaseReceivableTemplate/{taxLegalCaseReceivableTemplate} taxLegalCaseReceivableTemplate.destroy › TaxLegalReceivableTemplateContro…
export function deleteReceivableTemplate(templateId: ReceivableId) {
  return http.delete<ReceivableTemplate>(`${receivableTemplatesUrl}/${templateId}`);
}


